import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { get, post, put } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import Footer from "components/VerticalLayout/Footer";
import "./UserManagement.scss";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";

const AddRole = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [moduleData, setModuleData] = useState([]);
  const [operations, setOperations] = useState([]);
  const [checkedState, setCheckedState] = useState({});
  const [initialValues, setInitialValues] = useState({
    roleName: "",
    status: 1,
    description: "",
  });

  useEffect(() => {
    fetchData();
    if (roleId) {
      fetchRoleData(roleId);
    }
  }, [roleId]);

  const fetchData = async () => {
    try {
      const moduleList = await get(api.GET_ALL_MODULES);
      const operationsData = await get(api.GET_ALL_OPERATIONS);

      if (moduleList && operationsData) {
        const modules = moduleList?.modulePage?.content || [];
        const operations = operationsData?.operationPage || [];
        setModuleData(modules);
        setOperations(operations);

        const initialCheckedState = {};

        // Process main modules
        modules.forEach((module) => {
          initialCheckedState[module.moduleId] = {};

          // Add operations for main module
          operations.forEach((operation) => {
            initialCheckedState[module.moduleId][operation.id] = false;
          });

          // Process child modules
          if (module.childModules) {
            module.childModules.forEach((childModule) => {
              initialCheckedState[childModule.moduleId] = {};

              // Add operations for child module
              operations.forEach((operation) => {
                initialCheckedState[childModule.moduleId][operation.id] = false;
              });
            });
          }
        });

        if (!roleId) {
          setCheckedState(initialCheckedState);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchRoleData = async (roleId) => {
    try {
      const roleData = await get(api.GET_ROLE_BY_ID(roleId));

      if (roleData) {
        // Update initial values
        setInitialValues({
          roleName: roleData.role,
          status: roleData.status,
          description: roleData.description,
        });

        const updatedCheckedState = { ...checkedState };

        // Loop through each module permission in the role data
        roleData.modulePermissions.forEach((modulePerm) => {
          // Check if the module exists in the current checkedState
          if (!updatedCheckedState[modulePerm.module.moduleId]) {
            updatedCheckedState[modulePerm.module.moduleId] = {};
          }

          // Loop through each operation in the module's operations
          modulePerm.operations.forEach((operation) => {
            // Set the operation's checked state to true
            updatedCheckedState[modulePerm.module.moduleId][
              operation.id
            ] = true;
          });

          // Handle child modules if any
          if (modulePerm.module.childModules) {
            modulePerm.module.childModules.forEach((childModule) => {
              // Initialize the state for child modules if not present
              if (!updatedCheckedState[childModule.moduleId]) {
                updatedCheckedState[childModule.moduleId] = {};
              }

              // Loop through each operation and set it to true for child modules
              modulePerm.operations.forEach((operation) => {
                updatedCheckedState[childModule.moduleId][operation.id] = true;
              });
            });
          }
        });

        // Update the state with the new checked states
        setCheckedState(updatedCheckedState);
      }
    } catch (error) {
      console.error("Error fetching role data:", error);
    }
  };

  const handleCheckboxChange = (moduleId, operationId) => {
    setCheckedState((prevState) => {
      // Create a deep copy of the previous state to avoid direct mutation
      const newState = JSON.parse(JSON.stringify(prevState));

      // Ensure the module exists in the state
      if (!newState[moduleId]) {
        newState[moduleId] = {};
      }

      // Toggle the specific operation's checked state
      newState[moduleId][operationId] = !newState[moduleId][operationId];

      return newState;
    });
  };

  // const handleRowCheckboxChange = (moduleId, isChecked) => {
  //   setCheckedState((prevState) => {
  //     const updatedState = { ...prevState };

  //     // Toggle all operations for the row (module)
  //     Object.keys(updatedState[moduleId]).forEach((operationId) => {
  //       updatedState[moduleId][operationId] = isChecked;
  //     });

  //     return updatedState;
  //   });
  // };

  const handleColumnCheckboxChange = (operationId, isChecked) => {
    setCheckedState((prevState) => {
      const updatedState = { ...prevState };

      // Toggle all modules for the column (operation)
      Object.keys(updatedState).forEach((moduleId) => {
        updatedState[moduleId][operationId] = isChecked;
      });

      return updatedState;
    });
  };

  // const isRowFullySelected = (moduleId) => {
  //   return Object.values(checkedState[moduleId] || {}).every(
  //     (isSelected) => isSelected
  //   );
  // };

  const isColumnFullySelected = (operationId) => {
    return Object.keys(checkedState).every(
      (moduleId) => checkedState[moduleId]?.[operationId]
    );
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: Yup.object({
      roleName: Yup.string()
        .required("Role name is required")
        .min(3, "Role name must be at least 3 characters long")
        .max(50, "Role name cannot exceed 50 characters")
        .matches(
          /^[a-zA-Z0-9\s_-]+$/,
          "Role name can only contain letters, numbers, spaces, underscores, and hyphens"
        ),
      status: Yup.number()
        .oneOf([0, 1], "Invalid status")
        .required("Status is required"),
      description: Yup.string().max(
        225,
        "Description cannot exceed 225 characters"
      ),
    }),

    onSubmit: async (values) => {
      const modulePermissions = Object.keys(checkedState)?.map((moduleId) => {
        const operations = Object.keys(checkedState[moduleId])
          .filter((operationId) => checkedState[moduleId][operationId])
          ?.map((id) => ({ id: parseInt(id) }));

        return {
          module: { moduleId: parseInt(moduleId) },
          operations: operations,
        };
      });

      const rolePermissionDTO = {
        role: values.roleName,
        status: values.status,
        description: values.description,
        modulePermissions: modulePermissions,
      };

      try {
        if (roleId) {
          await put(api.UPDATE_ROLE_BY_ID(roleId), { rolePermissionDTO });
          showSuccessToast("Role updated successfully");
        } else {
          await post(api.CREATE_ROLE, { rolePermissionDTO });
          showSuccessToast("Role created successfully");
        }
        navigate("/role");
      } catch (error) {
        console.error("Error saving role permissions:", error);
        showErrorToast("Something went wrong");
      }
    },
  });

  return (
    <React.Fragment>
      <div className="page-content ">
        <Container fluid={true} style={{ padding: "0px", gap: "10px" }}>
          {roleId ? (
            <PageSubHeader
              heading={"Edit Role"}
              routePath={"Home"}
              pageName={"Edit Role"}
            />
          ) : (
            <PageSubHeader
              heading={"Add Role"}
              routePath={"Home"}
              pageName={"Add Role"}
            />
          )}

          <Form onSubmit={validation.handleSubmit}>
            <Row className="px-4 mx-0 my-3 d-flex justify-content-between align-items-center">
              <FormGroup className="status">
                <Label for="roleName">
                  Role Name <span style={{ color: "red" }}>*</span>
                </Label>
                <Input
                  type="text"
                  id="roleName"
                  name="roleName"
                  placeholder="Role Name"
                  value={validation.values.roleName}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.roleName && validation.errors.roleName
                  }
                />
                {validation.touched.roleName && validation.errors.roleName ? (
                  <FormFeedback type="invalid">
                    {validation.errors.roleName}
                  </FormFeedback>
                ) : null}
              </FormGroup>

              <FormGroup className="status">
                <Label>
                  Status <span style={{ color: "red" }}>*</span>
                </Label>
                <select
                  name="status"
                  className="form-select"
                  id="formBasicStatus"
                  value={validation.values.status}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                >
                  <option value="1">Active</option>
                  <option value="0">In-Active</option>
                </select>
                {validation.touched.status && validation.errors.status ? (
                  <div className="text-danger">{validation.errors.status}</div>
                ) : null}
              </FormGroup>

              <FormGroup>
                <Label for="description">Description</Label>
                <Input
                  type="textarea"
                  maxLength={225}
                  rows={3}
                  name="description"
                  placeholder="Description"
                  value={validation.values.description}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                />
                {validation.touched.description &&
                validation.errors.description ? (
                  <div className="text-danger">
                    {validation.errors.description}
                  </div>
                ) : null}
              </FormGroup>

              <div className="Module m-0 p-0 add-role-table">
                <Row className="m-0 p-0">
                  <Col lg={12} className="m-0 p-0">
                    <Card className="m-0 p-0">
                      <CardBody className="m-0 p-0">
                        <div className="table-responsive">
                          <table className="table mb-0">
                            <thead>
                              <tr>
                                <th>
                                  <div className="d-flex align-items-center">
                                    Module
                                  </div>
                                </th>
                                {operations?.map((operation) => (
                                  <th
                                    key={operation.id}
                                    className="text-nowrap"
                                  >
                                    <div className="d-flex flex-column-reverse align-items-center">
                                      <Input
                                        type="checkbox"
                                        checked={isColumnFullySelected(
                                          operation.id
                                        )}
                                        onChange={(e) =>
                                          handleColumnCheckboxChange(
                                            operation.id,
                                            e.target.checked
                                          )
                                        }
                                      />
                                      {operation.operationName}
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {moduleData?.map((module) => (
                                <React.Fragment key={module.moduleId}>
                                  <tr>
                                    <td className="text-nowrap">
                                      {/* <Input
                                        type="checkbox"
                                        checked={isRowFullySelected(
                                          module.moduleId
                                        )}
                                        onChange={(e) =>
                                          handleRowCheckboxChange(
                                            module.moduleId,
                                            e.target.checked
                                          )
                                        }
                                      /> */}
                                      {module.moduleName}
                                    </td>
                                    {operations?.map((operation) => (
                                      <td key={operation.id}>
                                        <div className="d-flex justify-content-center">
                                          <Input
                                            type="checkbox"
                                            checked={
                                              checkedState[module.moduleId]?.[
                                                operation.id
                                              ] || false
                                            }
                                            onChange={() =>
                                              handleCheckboxChange(
                                                module.moduleId,
                                                operation.id
                                              )
                                            }
                                          />
                                        </div>
                                      </td>
                                    ))}
                                  </tr>
                                  {module.childModules?.map((childModule) => (
                                    <tr key={childModule.moduleId}>
                                      <td className="submodule-name text-nowrap">
                                        {/* <Input
                                          type="checkbox"
                                          checked={isRowFullySelected(
                                            childModule.moduleId
                                          )}
                                          onChange={(e) =>
                                            handleRowCheckboxChange(
                                              childModule.moduleId,
                                              e.target.checked
                                            )
                                          }
                                        /> */}
                                        - {childModule.moduleName}
                                      </td>
                                      {operations?.map((operation) => (
                                        <td key={operation?.id}>
                                          <div className="d-flex justify-content-center">
                                            <Input
                                              type="checkbox"
                                              checked={
                                                checkedState[
                                                  childModule.moduleId
                                                ]?.[operation?.id] || false
                                              }
                                              onChange={() =>
                                                handleCheckboxChange(
                                                  childModule.moduleId,
                                                  operation?.id
                                                )
                                              }
                                            />
                                          </div>
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </div>
            </Row>
            <Footer save={validation.handleSubmit} />
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddRole;
